import { ApolloError, gql } from 'apollo-boost';
import DatePicker from 'core/components/DatePicker';
import Select from 'core/components/Select';
import { showSnackbar } from 'core/components/snackbar';
import { button, buttonAccent, fieldInput } from 'core/style';
import injectSheet, { createStyles } from 'core/style/injectSheet';
import { ITheme, WithStyles } from 'core/style/interfaces';
import { getErrors } from 'core/utils/apollo';
import React, { useState } from 'react';
import {
    CurrencyEnum,
    Get52weeksDocument,
    Get52weeksQuery,
    Get52weeksQueryVariables,
    useStart52weeksMutation,
} from 'site/graphql';

gql`
    mutation Start52weeks($date: Date!, $sum: Decimal!, $target: String, $currency: CurrencyEnum!) {
        start52weeks(date: $date, sum: $sum, target: $target, currency: $currency) {
            id
            startDate
            startSum
            target
            currency
            investments {
                id
                sum
                weekNumber
            }
        }
    }
`;

export const currencyOptions: { value: CurrencyEnum; label: string }[] = [
    { value: CurrencyEnum.Rub, label: '₽' },
    { value: CurrencyEnum.Usd, label: '$' },
];

type IService52WeeksConfigProps = {} & WithStyles<typeof styles>;

const Service52WeeksConfig: React.FC<IService52WeeksConfigProps> = ({ classes }) => {
    const [target, setTarget] = useState('');
    const [sum, setSum] = useState('');
    const [sumType, setSumType] = useState<'start' | 'target'>('start');
    const [date, setDate] = useState(new Date());
    const [currency, setCurrency] = useState(CurrencyEnum.Rub);

    const handleChangeSum = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSum(e.target.value.replace(/\D/, ''));
    };

    const [start, { client, loading }] = useStart52weeksMutation({
        onCompleted: res =>
            client.writeQuery<Get52weeksQuery, Get52weeksQueryVariables>({
                query: Get52weeksDocument,
                data: {
                    get52weeks: res.start52weeks,
                },
            }),
        onError: (error: ApolloError) => showSnackbar({ snackbarErrors: getErrors(error) }),
    });

    const handleStart = () => {
        const parts = 1378; // Всего частей для накопления (1+2+3+…+52) = 1378
        const startSum = sumType === 'start' ? parseInt(sum, 10) : Math.ceil(parseInt(sum, 10) / parts);

        start({ variables: { currency, date: date.toISOString(), sum: startSum, target } });
    };

    return (
        <div className={classes.root}>
            <p className={classes.title}>Введи параметры для расчета:</p>

            <div className={classes.row}>
                <label htmlFor="s52-target">На что копишь</label>
                <input
                    id="s52-target"
                    type="text"
                    className={classes.input}
                    onChange={e => setTarget(e.target.value)}
                    value={target}
                    disabled={loading}
                />
            </div>
            <div className={classes.row}>
                <label>
                    Сумма,{' '}
                    <select
                        className={classes.select}
                        value={sumType}
                        onChange={e => setSumType(e.target.value as any)}
                    >
                        <option value="start">с которой начнешь копить</option>
                        <option value="target">которую хочешь накопить</option>
                    </select>
                </label>
                <div className={classes.sumBlock}>
                    <input
                        type="number"
                        inputMode="decimal"
                        className={classes.input}
                        onChange={handleChangeSum}
                        value={sum}
                        min={1}
                        step={10}
                        disabled={loading}
                    />
                    <Select
                        options={currencyOptions}
                        value={currency}
                        onChange={setCurrency as any}
                        styles={{ container: { width: 60, height: 42, marginLeft: 10, marginRight: 0 } }}
                        redLineOnSelect={false}
                        disabled={loading}
                    />
                </div>
            </div>
            <div className={classes.row}>
                <label htmlFor="s52-date">Дата начала</label>
                <DatePicker
                    id="s52-date"
                    className={classes.datePicker}
                    wrapperClassName={classes.datePickerWrapper}
                    value={date}
                    onChange={setDate}
                    disabled={loading}
                />
            </div>

            <button className={classes.button} onClick={handleStart} disabled={!sum || +sum === 0 || loading}>
                Ок
            </button>
        </div>
    );
};

const styles = (theme: ITheme) =>
    createStyles({
        root: {
            display: 'flex',
            flexDirection: 'column',
            fontSize: 16,
        },
        title: {
            fontWeight: 'bold',
        },
        row: {
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            marginBottom: 10,

            '& > label': {
                width: 400,

                [theme.breakpoints.down('xs')]: {
                    width: 'auto',
                    marginBottom: 5,
                },
            },
        },
        input: {
            ...fieldInput,
            width: 300,

            [theme.breakpoints.down('xs')]: {
                width: 100 + '%',
            },
        },
        sumBlock: {
            display: 'flex',

            '& input': {
                flex: 1,
            },

            [theme.breakpoints.down('xs')]: {
                width: 100 + '%',
            },
        },
        datePickerWrapper: {
            width: 330,

            [theme.breakpoints.down('xs')]: {
                width: 100 + '%',
            },
        },
        datePicker: {
            width: 100 + '%',
            height: 42,
            boxSizing: 'border-box',
        },
        button: {
            ...button(theme),
            ...buttonAccent(theme),
            margin: '1.5rem 0',
            alignSelf: 'center',
            width: 200,
        },
        select: {
            background: 'transparent',
            borderRadius: 0,
            borderBottomColor: theme.colors.accent,
            border: `1px dashed transparent`,
            cursor: 'pointer',
            padding: '0 5px',

            '-webkit-appearance': 'none',
            '-moz-appearance': 'none',

            '&:hover, &:focus': {
                outline: 'none',
                borderColor: theme.colors.accent,
            },
        },
    });

export default injectSheet(styles)(Service52WeeksConfig);
